// #region Imports
import { createSelector, createSlice } from "@reduxjs/toolkit";
import { RootState } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import { AvatarSize, DEFAULT_AVATAR_SIZE, getAvatarCacheKey } from "marketplace/_views/avatars/avatar-size";
import { companiesApi } from "marketplace/companies/_api/company-api";
import MarketplaceCompanySearchSchema, {
  MarketplaceCompanySearchJSON,
} from "marketplace/companies/_api/_schema/marketplace-company-search-schema";
import Company from "marketplace/companies/company";
import CompanySchema, { CompanyJSON } from "marketplace/companies/company-schema";
// #endregion

// #region Store State Type
export type CompanyStoreState = {
  vendors: {
    entries?: CompanyJSON[];
  };
  byId: {
    entries: Record<string, CompanyJSON>;
  };
  byQuery: {
    entries: Record<string, CompanyJSON[]>;
  };
  avatars: {
    entries: Record<string, string>;
  };
};
// #endregion

// #region Initial State
const initialState: CompanyStoreState = {
  vendors: {
    entries: undefined,
  },
  byId: {
    entries: {},
  },
  byQuery: {
    entries: {},
  },
  avatars: {
    entries: {},
  },
};
// #endregion

// #region Slice
export const companiesSlice = createSlice({
  name: "companies",
  initialState,
  // #region Simple Reducers
  reducers: {
    addCompany: (state, action) => {
      const companyId = action.payload.id?.value;
      if (!companyId) return;

      state.byId.entries[companyId] = action.payload;
      state.vendors.entries = state.vendors.entries?.filter((company) => company.id !== companyId) ?? [];
      state.vendors.entries.push(action.payload);
    },
    addCompanyJSON: (state, action: { payload: CompanyJSON }) => {
      const companyJSON = action.payload;
      if (!companyJSON.id) return;

      state.byId.entries[companyJSON.id] = companyJSON;
      state.vendors.entries = state.vendors.entries?.filter((company) => company.id !== companyJSON.id) ?? [];
      state.vendors.entries.push(companyJSON);
    },
    removeCompany: (state, action) => {
      delete state.byId.entries[action.payload.value];
    },
  },
  // #endregion
  // #region Async Reducers
  extraReducers: (builder) => {
    // getCompanyVendors
    builder.addMatcher(companiesApi.endpoints.getCompanyVendors.matchFulfilled, (state, action) => {
      state.vendors.entries = action.payload;
    });

    // getCompanyById
    builder.addMatcher(companiesApi.endpoints.getCompanyById.matchFulfilled, (state, action) => {
      const companyId = action.meta.arg.originalArgs.companyId;
      if (!action.payload) {
        console.error("Company not received by store.");
        return;
      }
      state.byId.entries[companyId] = action.payload;
    });

    // getCompaniesByIds
    builder.addMatcher(companiesApi.endpoints.getCompaniesByIds.matchFulfilled, (state, action) => {
      for (const company of action.payload.found) {
        state.byId.entries[company.id] = company;
      }
    });

    // searchCompanies
    builder.addMatcher(companiesApi.endpoints.searchCompanies.matchFulfilled, (state, action) => {
      const cacheKey = MarketplaceCompanySearchSchema.toCacheKey(action.meta.arg.originalArgs.search);
      state.byQuery.entries[cacheKey] =
        action.payload.hits
          ?.map((hit) => CompanySchema.fromMarketplaceSearchInfo(hit.data))
          .filter((company): company is CompanyJSON => company !== undefined) ?? [];
    });

    // getCompanyAvatar
    builder.addMatcher(companiesApi.endpoints.getCompanyAvatar.matchFulfilled, (state, action) => {
      const { companyId, size = DEFAULT_AVATAR_SIZE } = action.meta.arg.originalArgs;
      state.avatars.entries[getAvatarCacheKey(companyId, size)] = action.payload;
      if(size === DEFAULT_AVATAR_SIZE) state.avatars.entries[companyId] = action.payload;
    });

    // createCompanyAvatar
    builder.addMatcher(companiesApi.endpoints.createCompanyAvatar.matchFulfilled, (state, action) => {
      const companyId = action.meta.arg.originalArgs.companyId;
      delete state.avatars.entries[companyId];
      delete state.avatars.entries[getAvatarCacheKey(companyId, "small")];
      delete state.avatars.entries[getAvatarCacheKey(companyId, "medium")];
      delete state.avatars.entries[getAvatarCacheKey(companyId, "large")];
    });
    builder.addMatcher(companiesApi.endpoints.createCompanyAvatar.matchRejected, (state, action) => {
      const companyId = action.meta.arg.originalArgs.companyId;
      state.avatars.entries[companyId] = "";
      state.avatars.entries[getAvatarCacheKey(companyId, "small")] = "";
      state.avatars.entries[getAvatarCacheKey(companyId, "medium")] = "";
    });

    // createCompany
    builder.addMatcher(companiesApi.endpoints.createCompany.matchFulfilled, (state, action) => {
      const companyId = action.payload.id;
      state.byId.entries[companyId] = action.payload;
    });

    // updateCompany
    builder.addMatcher(companiesApi.endpoints.updateCompany.matchFulfilled, (state, action) => {
      const companyId = action.payload.id;
      state.byId.entries[companyId] = action.payload;
      state.byQuery = { entries: {} };
      state.vendors.entries = undefined;
    });
  },
  // #endregion
});
// #endregion

// #region Exports

export const { addCompany, addCompanyJSON, removeCompany } = companiesSlice.actions;

// #region Company Vendors
const selectCompanyVendors = createSelector([(state: RootState) => state.companies.vendors.entries], (entries) =>
  !entries ? undefined : entries.map((json) => Company.fromJSON(json)).filter((c): c is Company => !!c),
);
export const getCompanyVendors = () => (state: RootState) => selectCompanyVendors(state);
// #endregion

// #region Company By Id
const selectCompanyById = createSelector(
  [(state: RootState) => state.companies.byId.entries, (_: RootState, id?: Guid) => id],
  (entries, id) => {
    if (!id) return undefined;
    const companyJson = entries[id.value];
    if (!companyJson) return undefined;
    return Company.fromJSON(companyJson);
  },
);
export const getCompanyById = (id?: Guid) => (state: RootState) => selectCompanyById(state, id);
// #endregion

// #region Companies By Ids
const selectCompaniesByIds = createSelector(
  [(state: RootState) => state.companies.byId.entries, (_: RootState, ids: Guid[]) => ids],
  (entries, ids) =>
    ids
      .map((id) => entries[id.value])
      .filter((x): x is CompanyJSON => !!x)
      .map((json) => Company.fromJSON(json)),
);
export const getCompaniesByIds = (ids: Guid[]) => (state: RootState) => selectCompaniesByIds(state, ids);
// #endregion

// #region Companies By Query
export const getCompaniesByQuery = (search: MarketplaceCompanySearchJSON) => (state: RootState) =>
  state.companies.byQuery.entries[MarketplaceCompanySearchSchema.toCacheKey(search)];
// #endregion

// #region Company Avatar
export const getCompanyAvatar = (companyId: Guid | null, size: AvatarSize = DEFAULT_AVATAR_SIZE) => (state: RootState) =>
  companyId
    ? state.companies.avatars.entries[getAvatarCacheKey(companyId.value, size)] ??
      (size === DEFAULT_AVATAR_SIZE ? state.companies.avatars.entries[companyId.value] : undefined)
    : undefined;
// #endregion

// #endregion
